import { Canvas, Group, Rect } from 'fabric';
import type { ITextboxCustom } from '@/infrastructure/engine2d/types/ITextboxCustom';

type TCloneResult = {
    canvas: Canvas;
    // The unnamed Group holding the working-zone Rect — the caller
    // uses it to derive print dimensions and to remove it before
    // rendering the final output.
    workingZoneGroup: Group | undefined;
    workingZoneRect: Rect | undefined;
};

// Clone the fabric canvas so downstream mutations (removing overlays,
// resizing the viewport, etc.) don't touch the live editor. Fabric's
// clone() drops textbox `styles` in some paths, so we re-apply them
// from the source by id after cloning.
async function cloneCanvasForExport(source: Canvas): Promise<TCloneResult> {
    source.renderAll();
    const canvas = await source.clone(['objects', 'width', 'height', 'viewportTransform', 'id']);

    const originalTexts = source.getObjects('textbox') as ITextboxCustom[];
    const clonedTexts = canvas.getObjects('textbox') as ITextboxCustom[];
    clonedTexts.forEach((clonedText) => {
        const originalTextbox = originalTexts.find((txt) => txt.id === clonedText.id);
        if (originalTextbox) {
            clonedText.set({ styles: { ...originalTextbox.styles } });
        }
    });

    // The working-zone group is the sole top-level object created
    // without an `id` — everything the editor adds (fields, grid,
    // cutting group, background mask) carries one.
    const workingZoneGroup = canvas.getObjects().find((object): object is Group => {
        return !(object as Group & { id: string }).id;
    });
    const workingZoneRect = workingZoneGroup?._objects.find((obj) => obj.type === 'rect') as Rect | undefined;

    return { canvas, workingZoneGroup, workingZoneRect };
}

export { cloneCanvasForExport };
